import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useApolloClient, useQuery } from '@apollo/react-hooks'
import { History, LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import * as Query from '../../../api/gql/query'
import { describeCostControlError } from '../lib/errors'
import { formatMoney } from '../lib/money'
import {
  HISTORY_PAGE, activityPath, describeActivity, entityLabelKey, hasMoreHistory, historyVariables, mergeHistory, projectHistoryVariables, reconcileOlderPages,
} from '../lib/records'
import type { CostActivityEntry, CostEntityType } from '../types'
import RecordHistorySkeleton from './RecordHistorySkeleton'

interface Props {
  projectId: string
  currency: string
  locale: string
  /** One record's history; leave both out for the whole project's (1b-C spec §5). */
  entityType?: CostEntityType
  entityId?: string
  /** Project history only: the entity types to show; undefined = all. */
  entityTypes?: string[]
  /** Shown next to the title (the project history's filter). */
  headerExtra?: React.ReactNode
}

/**
 * A record's history, or the whole project's, newest first (ruling Q13). The first page is a live query with the variables
 * the write hooks reload; older pages are fetched on demand and merged by id.
 */
const RecordHistoryCard: React.FC<Props> = ({ projectId, currency, locale, entityType, entityId, entityTypes, headerExtra }) => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const project = !entityType || !entityId
  const variablesAt = (skip: number) => (project ? projectHistoryVariables(projectId, entityTypes, skip) : historyVariables(projectId, entityType!, entityId!, skip))
  const { data, loading, error } = useQuery<{ costActivityLog: CostActivityEntry[] }>(Query.COST_ACTIVITY_LOG, {
    variables: variablesAt(0),
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const [older, setOlder] = useState<CostActivityEntry[][]>([])
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [olderError, setOlderError] = useState<string | null>(null)
  const typesKey = entityTypes ? entityTypes.join(',') : ''
  useEffect(() => { setOlder([]); setOlderError(null) }, [projectId, entityType, entityId, typesKey])

  const first = data?.costActivityLog
  const firstId = first?.[0]?.id ?? null
  const prevFirstId = useRef<string | null>(null)
  useEffect(() => {
    setOlder((pages) => reconcileOlderPages(prevFirstId.current, firstId, pages))
    prevFirstId.current = firstId
  }, [firstId])
  const entries = first ? mergeHistory(first, older) : undefined
  const lastPage = older.length ? older[older.length - 1] : first ?? []
  const money = (v: string) => formatMoney(v, { locale, currency })

  const loadOlder = async () => {
    setLoadingOlder(true)
    setOlderError(null)
    try {
      const { data: page } = await client.query<{ costActivityLog: CostActivityEntry[] }>({
        query: Query.COST_ACTIVITY_LOG,
        variables: variablesAt(HISTORY_PAGE * (older.length + 1)),
        fetchPolicy: 'network-only',
        context: { handlesErrors: true },
      })
      setOlder((pages) => [...pages, page.costActivityLog])
    } catch (e: any) {
      setOlderError(describeCostControlError(e?.message, t))
    } finally {
      setLoadingOlder(false)
    }
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4" />
          {t('CostControl_history_title')}
        </CardTitle>
        {headerExtra}
      </CardHeader>
      <CardContent>
        {!entries && loading && <RecordHistorySkeleton />}
        {!entries && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
        {entries && entries.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_history_empty')}</p>}
        {entries && entries.length > 0 && (
          <ol className="space-y-3">
            {entries.map((e) => {
              const d = describeActivity(e, t, money)
              const typeKey = project ? entityLabelKey(e.entityType) : null
              const path = project ? activityPath(projectId, e) : null
              return (
                <li key={e.id} className="text-sm">
                  {project && (typeKey || e.subject) && (
                    <div className="text-xs text-muted-foreground">
                      {typeKey && <span>{t(typeKey)}</span>}
                      {typeKey && e.subject && <span> · </span>}
                      {e.subject && (path ? <Link to={path} className="text-primary hover:underline">{e.subject}</Link> : <span>{e.subject}</span>)}
                    </div>
                  )}
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">{d.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {e.user?.name ?? '—'} · {new Date(e.createdAt).toLocaleString(t('date_locale') || 'cs-CZ')}
                    </span>
                  </div>
                  {d.detail && <div className="text-muted-foreground">{d.detail}</div>}
                </li>
              )
            })}
          </ol>
        )}
        {olderError && <p className="mt-2 text-sm text-destructive">{olderError}</p>}
        {entries && hasMoreHistory(lastPage.length) && (
          <Button variant="outline" size="sm" className="mt-3" onClick={loadOlder} disabled={loadingOlder}>
            {loadingOlder && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_history_older')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
export default RecordHistoryCard
